/**
 * Учебные единицы и агрегированное состояние навыка.
 *
 * ТЗ FR-EN-03, §11.3 (MasteryUnit / MasteryStat), ARCHITECTURE §7.1.
 * Состояние агрегированное: поток нажатий в базу не сохраняется.
 */

import type { LanguageCode, LayoutId } from './language.js';
import type { MasteryUnitId } from './ids.js';

/** Вид учебной единицы. */
export const MASTERY_UNIT_KINDS = [
  'key',
  'bigram',
  'trigram',
  'word',
  'punctuation',
  'pattern',
] as const;
export type MasteryUnitKind = (typeof MASTERY_UNIT_KINDS)[number];

/** Учебная единица: то, что можно освоить и повторять. */
export interface MasteryUnit {
  readonly id: MasteryUnitId;
  readonly kind: MasteryUnitKind;
  /** Текстовое содержание единицы: символ, сочетание или слово. */
  readonly content: string;
  readonly language: LanguageCode;
  readonly layoutId: LayoutId;
  /** Частота в реальном языке, 0..1. Для служебных единиц — 0. */
  readonly realWorldFrequency: number;
}

/** Агрегированная статистика освоения одной единицы. */
export interface MasteryStat {
  readonly unitId: MasteryUnitId;
  readonly attempts: number;
  readonly firstTryCorrect: number;
  /** Доля ошибок, 0..1. */
  readonly errorRate: number;
  readonly medianLatencyMs: number;
  readonly p95LatencyMs: number;
  /** Доля попыток с исправлением, 0..1. */
  readonly correctionRate: number;
  /** Итоговая оценка освоенности, 0..1. */
  readonly masteryScore: number;
  readonly consecutiveSuccesses: number;
  /** Календарная метка последнего показа (ISO 8601). */
  readonly lastSeenAt: string;
  /** Календарная метка следующего повторения (ISO 8601). */
  readonly nextReviewAt: string;
  readonly layoutId: LayoutId;
  readonly language: LanguageCode;
}

/** Начальная статистика для ещё не показанной единицы. */
export function emptyStat(
  unitId: MasteryUnitId,
  language: LanguageCode,
  layoutId: LayoutId,
  now: string,
): MasteryStat {
  return {
    unitId,
    attempts: 0,
    firstTryCorrect: 0,
    errorRate: 0,
    medianLatencyMs: 0,
    p95LatencyMs: 0,
    correctionRate: 0,
    masteryScore: 0,
    consecutiveSuccesses: 0,
    lastSeenAt: now,
    nextReviewAt: now,
    layoutId,
    language,
  };
}

/** Порог, начиная с которого единица считается освоенной (ТЗ §7.7). */
export const MASTERED_THRESHOLD = 0.8;

/** Порог, ниже которого единица считается слабой и попадает в приоритет. */
export const WEAK_THRESHOLD = 0.5;
